"use client"

import { useState, useEffect, createContext, useContext } from 'react'
import { useRouter } from 'next/navigation'
import { createBrowserClient } from '@/lib/supabase'
import { User, Session } from '@supabase/supabase-js'

interface AuthContextType {
  user: User | null
  session: Session | null
  isLoading: boolean
  signIn: (email: string, password: string) => Promise<{
    error: Error | null
    data: { user: User | null; session: Session | null } | null
  }>
  signUp: (email: string, password: string, userData?: object) => Promise<{
    error: Error | null
    data: { user: User | null; session: Session | null } | null
  }>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<{
    error: Error | null
    data: any
  }>
  processStripeSubscription: (userId: string) => Promise<any>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const router = useRouter()

  useEffect(() => {
    const supabase = createBrowserClient()
    
    const setData = async () => {
      const { data: { session }, error } = await supabase.auth.getSession()
      if (error) {
        console.error(error)
        setSession(null)
        setUser(null)
      } else {
        setSession(session)
        setUser(session?.user ?? null)
      }
      setIsLoading(false)
    }

    setData()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session)
        setUser(session?.user ?? null)
        setIsLoading(false)
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    const supabase = createBrowserClient()
    return supabase.auth.signInWithPassword({ email, password })
  }

  const signUp = async (email: string, password: string, userData: Record<string, any> = {}) => {
    const supabase = createBrowserClient()
    
    // Extrair e tratar dados especiais que precisam de processamento separado
    const inviteCode = userData.inviteCode;
    const metadataToSave = { ...userData };
    
    // Garantir que fullName exista
    if (!metadataToSave.fullName && metadataToSave.firstName && metadataToSave.lastName) {
      metadataToSave.fullName = `${metadataToSave.firstName} ${metadataToSave.lastName}`;
    }
    
    // Remover inviteCode dos metadados, mas manter o restante
    if ('inviteCode' in metadataToSave) {
      delete metadataToSave.inviteCode;
    }

    console.log('Salvando metadados durante registro:', metadataToSave);
    
    const result = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadataToSave,
      },
    })
    
    // Se o registro for bem-sucedido e houver um convite
    if (result.data?.user && inviteCode) {
      try {
        console.log('Processando código de convite:', inviteCode);
        const formData = new FormData()
        formData.append('inviteCode', inviteCode)
        
        // Esta chamada simplesmente registra na sessão que o usuário tem um convite pendente
        // O verify-invite processará o convite quando o usuário confirmar o email
        const res = await fetch('/api/auth/verify-invite', {
          method: 'POST',
          body: formData,
        })
        
        if (!res.ok) {
          console.warn('Aviso: Código de convite presente, mas não foi possível verificar')
        }
      } catch (error) {
        console.error('Erro ao processar código de convite:', error)
      }
    }
    
    return result
  }

  const signOut = async () => {
    const supabase = createBrowserClient()
    await supabase.auth.signOut()
    router.push('/auth/login')
  }

  const resetPassword = async (email: string) => {
    const supabase = createBrowserClient()
    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    })
  }
  
  // Função para processar a assinatura do Stripe após confirmação de email
  const processStripeSubscription = async (userId: string) => {
    try {
      console.log('Processando assinatura para usuário:', userId);
      
      // Definir caminho completo da API
      const apiPath = '/api/auth/process-subscription';
      console.log('Chamando API:', apiPath);
      
      const response = await fetch(apiPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      })
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro na resposta da API:', response.status, errorText);
        throw new Error(`Falha ao processar assinatura: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Resposta da API:', data);
      return data;
    } catch (error) {
      console.error('Erro ao processar assinatura:', error)
      return { error }
    }
  }

  const value = {
    user,
    session,
    isLoading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    processStripeSubscription,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 